// src/components/Experiment/DataGenerationDialog.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Card, Badge, Spinner, Alert } from 'react-bootstrap';
import JobAnalysisCard from './JobAnalysisCard';
import useImageWithAuth from '../../hooks/useImageWithAuth';

const DataGenerationDialog = ({ show, onHide, experiment, onGenerate, onUpdate }) => {
  const [generationMode, setGenerationMode] = useState('by_sample');
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [jobsWithCalculateTypes, setJobsWithCalculateTypes] = useState([]);
  const [jobStatuses, setJobStatuses] = useState({});
  const [globalCalculateType, setGlobalCalculateType] = useState('rgb'); // New state for global selection

  useEffect(() => {
    if (show && experiment.selectedJobs) {
      // Initialize jobs with their current calculate types
      const jobsWithTypes = experiment.selectedJobs.map(job => ({
        ...job,
        calculate_type: job.calculate_type || 'rgb' // Default to rgb if not set
      }));

      setJobsWithCalculateTypes(jobsWithTypes);

      // Initialize with all jobs selected
      const initialSelected = jobsWithTypes.map(job => job.id);
      setSelectedJobIds(initialSelected);

      // Check job statuses
      const statuses = {};
      jobsWithTypes.forEach(job => {
        statuses[job.id] = checkJobStatus(job, experiment.plateSchema);
      });
      setJobStatuses(statuses);
    }
  }, [show, experiment]);

  const checkJobStatus = (job, plateSchema) => {
    // Check if grid dimensions and crop type match plate schema
    const plateRows = plateSchema?.rows || 0;
    const plateCols = plateSchema?.cols || 0;

    if (!job.grid_dimensions || !Array.isArray(job.grid_dimensions)) {
      return { ok: false, reason: 'Missing grid dimensions' };
    }

    const [jobRows, jobCols] = job.grid_dimensions;
    const cropType = job.crop_type_display || 'Uncropped';

    // Check if job can match plate schema based on crop type
    const canMatch = canJobMatchPlate(jobRows, jobCols, cropType, plateRows, plateCols);

    return {
      ok: canMatch,
      reason: canMatch ? 'OK' : `Grid mismatch: ${jobRows}x${jobCols} with ${cropType} crop`
    };
  };

  const canJobMatchPlate = (jobRows, jobCols, cropType, plateRows, plateCols) => {
    if (jobRows === plateRows && jobCols === plateCols) return true;

    // Logic to determine if job can match plate based on crop type
    switch (cropType) {
      case 'Uncropped':
        return jobRows === plateRows && jobCols === plateCols;
      case 'Top':
      case 'Bottom':
        return jobRows === plateRows - 1 && jobCols === plateCols;
      case 'Left':
      case 'Right':
        return jobRows === plateRows && jobCols === plateCols - 1;
      case 'Top left':
      case 'Top right':
      case 'Bottom left':
      case 'Bottom right':
        return jobRows === plateRows - 1 && jobCols === plateCols - 1;
      default:
        return false;
    }
  };

  const handleJobSelection = (jobId, selected) => {
    if (selected) {
      setSelectedJobIds(prev => [...prev, jobId]);
    } else {
      setSelectedJobIds(prev => prev.filter(id => id !== jobId));
    }
  };

  const handleCalculateTypeChange = (jobId, calculateType) => {
    setJobsWithCalculateTypes(prev =>
      prev.map(job =>
        job.id === jobId
          ? { ...job, calculate_type: calculateType }
          : job
      )
    );

    // Also update the parent experiment if onUpdate is provided
    if (onUpdate) {
      const updatedJobs = jobsWithCalculateTypes.map(job =>
        job.id === jobId
          ? { ...job, calculate_type: calculateType }
          : job
      );
      onUpdate({ selectedJobs: updatedJobs });
    }
  };

  // New function to apply global calculate type to all selected jobs
  const handleGlobalCalculateTypeChange = (calculateType) => {
    setGlobalCalculateType(calculateType);

    // Apply to all selected jobs
    const updatedJobs = jobsWithCalculateTypes.map(job =>
      selectedJobIds.includes(job.id)
        ? { ...job, calculate_type: calculateType }
        : job
    );

    setJobsWithCalculateTypes(updatedJobs);

    // Also update the parent experiment if onUpdate is provided
    if (onUpdate) {
      onUpdate({ selectedJobs: updatedJobs });
    }
  };

  const handleGenerate = () => {
    // Get only selected jobs that are valid
    const selectedValidJobs = jobsWithCalculateTypes.filter(job =>
      selectedJobIds.includes(job.id) && jobStatuses[job.id]?.ok
    );

    if (selectedValidJobs.length === 0) {
      alert('Please select at least one valid job');
      return;
    }

    // Update parent with the current job configurations before generating
    if (onUpdate) {
      onUpdate({ selectedJobs: jobsWithCalculateTypes });
    }

    // Pass the jobs with their calculate types to the generation function
    onGenerate(generationMode, selectedValidJobs);
  };

  const getSelectedValidJobs = () => {
    return jobsWithCalculateTypes.filter(job =>
      selectedJobIds.includes(job.id) && jobStatuses[job.id]?.ok
    );
  };

  const getSelectedInvalidJobs = () => {
    return jobsWithCalculateTypes.filter(job =>
      selectedJobIds.includes(job.id) && !jobStatuses[job.id]?.ok
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Generate Experiment Data</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-4">
              <Form.Label>Generation Mode</Form.Label>
              <Form.Select
                value={generationMode}
                onChange={(e) => setGenerationMode(e.target.value)}
              >
                <option value="by_sample">Group by Sample</option>
                <option value="by_grid">Grid Order</option>
              </Form.Select>
              <Form.Text className="text-muted">
                {generationMode === 'by_sample'
                  ? 'Rows grouped by sample, then by coordinates within sample'
                  : 'Rows ordered by grid coordinates (row then column)'}
              </Form.Text>
            </Form.Group>

            <div className="mb-3">
              <h6>Job Selection Summary</h6>
              <div className="mb-2">
                <Badge bg="primary" className="me-2">
                  Total Selected: {selectedJobIds.length}
                </Badge>
              </div>
              <div className="mb-2">
                <Badge bg="success" className="me-2">
                  Valid: {getSelectedValidJobs().length}
                </Badge>
                <Badge bg="danger" className="me-2">
                  Invalid: {getSelectedInvalidJobs().length}
                </Badge>
              </div>
              <small className="text-muted">
                Only valid jobs will be used for data generation
              </small>
            </div>

            <div className="d-grid">
              <Button
                variant="primary"
                size="lg"
                onClick={handleGenerate}
                disabled={getSelectedValidJobs().length === 0}
              >
                Generate Data
                <br />
                <small>({getSelectedValidJobs().length} jobs)</small>
              </Button>
            </div>
          </Col>

          <Col md={8}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6>Available Jobs ({jobsWithCalculateTypes.length})</h6>
              <div className="d-flex align-items-center gap-2">
                {/* Global Calculate Type Selector */}
                <Form.Select
                    size="sm"
                    value={globalCalculateType}
                    onChange={(e) => handleGlobalCalculateTypeChange(e.target.value)}
                    style={{width: 'auto'}}
                >
                  <option value="rgb">RGB</option>
                  <option value="intensity">Intensity</option>
                  <option value="rg_intensity">RG Intensity</option>
                  <option value="rb_intensity">RB Intensity</option>
                  <option value="gb_intensity">GB Intensity</option>
                  <option value="r">R</option>
                  <option value="g">G</option>
                  <option value="b">B</option>
                </Form.Select>

                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setSelectedJobIds(jobsWithCalculateTypes.map(job => job.id))}
                >
                  Select All
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setSelectedJobIds([])}
                >
                  Clear All
                </Button>
              </div>
            </div>

            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {jobsWithCalculateTypes.length === 0 ? (
                <Alert variant="info">
                  <i className="bi bi-info-circle me-2"></i>
                  No jobs selected in the experiment. Please go back to the Job Selection stage.
                </Alert>
              ) : (
                jobsWithCalculateTypes.map(job => (
                  <JobAnalysisCard
                    key={job.id}
                    job={job}
                    selected={selectedJobIds.includes(job.id)}
                    status={jobStatuses[job.id]}
                    calculateType={job.calculate_type}
                    onSelectionChange={handleJobSelection}
                    onCalculateTypeChange={(type) => handleCalculateTypeChange(job.id, type)}
                  />
                ))
              )}
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleGenerate}
          disabled={getSelectedValidJobs().length === 0}
        >
          Generate Data ({getSelectedValidJobs().length} jobs)
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DataGenerationDialog;